import { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { KaziAtlasLogo } from './KaziAtlasLogo';
import { useTheme } from '../hooks/useTheme';
import { usePageTitle } from '../hooks/usePageTitle';

export const publicInfoStyles: Record<string, CSSProperties> = {
    paragraph: { margin: '0 0 1rem', lineHeight: 1.75 },
    list: { paddingLeft: '1.25rem', lineHeight: 1.75 },
    link: { color: 'var(--accent-text)', textDecoration: 'underline', textUnderlineOffset: 3 },
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '1.25rem' },
};

export function PublicInfoSection({ title, children }: { title: string; children: ReactNode }) {
    return <section style={{ marginTop: '2rem' }}>
        <h2 style={{ color: 'var(--ink)', fontSize: '1.3rem', margin: '0 0 0.75rem' }}>{title}</h2>
        {children}
    </section>;
}

export function PublicInfoPage({ title, intro, children }: { title: string; intro: string; children: ReactNode }) {
    usePageTitle(title);
    const { theme } = useTheme();
    return <div style={{ background: 'var(--bg)', color: 'var(--ink-body)', fontFamily: 'var(--font-body)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <header style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
            <nav aria-label="Main" style={{ maxWidth: 820, margin: '0 auto', padding: '1rem 1.25rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <Link to="/" aria-label="KaziAtlas home"><KaziAtlasLogo size={28} onLight={theme === 'light'} /></Link>
                <Link to="/" style={publicInfoStyles.link}>Back to home</Link>
            </nav>
        </header>
        <main style={{ maxWidth: 820, width: '100%', boxSizing: 'border-box', margin: '0 auto', padding: '3rem 1.25rem 4rem', flex: 1, overflowWrap: 'anywhere' }}>
            <h1 style={{ color: 'var(--ink)', fontSize: 'clamp(2rem, 5vw, 2.75rem)', lineHeight: 1.15, margin: '0 0 1rem' }}>{title}</h1>
            <p style={{ ...publicInfoStyles.paragraph, fontSize: '1.05rem' }}>{intro}</p>
            {children}
        </main>
        <footer style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '1.5rem 1.25rem' }}>
            <nav aria-label="Policies and support" style={{ maxWidth: 820, margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '1rem 1.5rem' }}>
                {[
                    ['/app-privacy', 'App privacy'], ['/privacy', 'Privacy policy'],
                    ['/availability', 'Availability'], ['/terms', 'Terms of service'],
                    ['/delete-account', 'Delete account'], ['/support', 'Support'],
                ].map(([to, label]) => <Link key={to} to={to} style={publicInfoStyles.link}>{label}</Link>)}
            </nav>
        </footer>
    </div>;
}
